import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useLogout } from 'api/authApi';
import { useGetAutoMiningStatus } from 'api/autoMiningApi';
import { usePairInventory } from 'api/exchangeApi';
import {
  CraftIcon,
  DiamondIcon,
  ExchangeIcon,
  HouseIcon,
  LogoutIcon,
  MytoolsIcon,
  ProfileIcon,
  UExchangeIcon,
  WalletIcon,
} from 'assets/images/index.js';
import { useCrossAppNavigation } from 'hooks/useCrossAppNavigation.js';
import TrueCalendar from 'utils/TrueCalendar';

import NavItem from './NavItem.jsx';
import UseDiamondModal from './UseDiamondModal.jsx';

import { CRAFT, CONVERT, GAME, MYTOOLS } from 'constants/routes.js';
import './style.scss';

const formatTime = (totalSeconds) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const getNow = () => (TrueCalendar.isSynced ? TrueCalendar.getCurrentTime() : Date.now());

const resolveEndMs = (status) => {
  if (!status || !status.isActive) return null;
  if (!status.endTime) return null;
  return new Date(status.endTime).getTime();
};

const BottomNavbar = () => {
  const { t } = useTranslation();
  const { handleOnClick } = useCrossAppNavigation();
  const [showDiamondModal, setShowDiamondModal] = useState(false);
  const [timerLabel, setTimerLabel] = useState(t('bottomNavBar.useDiamond'));
  const intervalRef = useRef(null);
  const logoutMutation = useLogout();

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  const { data: { data: { data: pairList = [] } = {} } = {} } = usePairInventory();
  const diamondPairId = pairList.find((p) => p.fullName?.toLowerCase() === 'diamond')?.pairId;

  const { data: statusData } = useGetAutoMiningStatus(diamondPairId);

  useEffect(() => {
    clearInterval(intervalRef.current);

    const status = statusData?.data?.data;
    const endMs = resolveEndMs(status);

    if (!endMs) {
      setTimerLabel(t('bottomNavBar.useDiamond'));
      return;
    }

    const tick = () => {
      const remaining = Math.max(0, Math.floor((endMs - getNow()) / 1000));
      if (remaining <= 0) {
        clearInterval(intervalRef.current);
        setTimerLabel(t('bottomNavBar.useDiamond'));
      } else {
        setTimerLabel(formatTime(remaining));
      }
    };

    tick();
    intervalRef.current = setInterval(tick, 1000);

    return () => clearInterval(intervalRef.current);
  }, [statusData, t]);

  const menuItems = [
    { to: GAME, name: t('bottomNavBar.home'), icon: HouseIcon },
    { to: CRAFT, name: t('bottomNavBar.craftTools'), icon: CraftIcon },
    { to: MYTOOLS, name: t('bottomNavBar.myTools'), icon: MytoolsIcon },
    { onClick: () => handleOnClick('Wallet'), name: t('bottomNavBar.wallet'), icon: WalletIcon },
    { to: CONVERT, name: t('bottomNavBar.exchange'), icon: ExchangeIcon },
    { onClick: () => handleOnClick('Trade'), name: t('bottomNavBar.zogoex'), icon: UExchangeIcon },
    { onClick: () => handleOnClick('Profile'), name: t('bottomNavBar.profile'), icon: ProfileIcon },
    { name: timerLabel, icon: DiamondIcon, onClick: () => setShowDiamondModal(true) },
    { name: t('bottomNavBar.logout'), icon: LogoutIcon, onClick: handleLogout },
  ];

  return (
    <>
      <div className="bottom-navbar">
        {menuItems.map((item, index) => (
          <NavItem icon={item.icon} key={index} name={item.name} onClick={item.onClick} to={item.to} />
        ))}
      </div>
      <UseDiamondModal onClose={() => setShowDiamondModal(false)} pairId={diamondPairId} show={showDiamondModal} />
    </>
  );
};

export default BottomNavbar;
